import {
  isGone,
  isReference,
  normalizeOperationOutcome,
  resolveId,
} from '@medplum/core'
import type {
  ExtractResource,
  Reference,
  ResourceType,
} from '@medplum/fhirtypes'
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseFhirReadOptions<T extends ResourceType> {
  query?: Omit<
    UseQueryOptions<
      ExtractResource<T>,
      unknown,
      ExtractResource<T>,
      ReturnType<(typeof cacheKeys)['read']>
    >,
    'initialData' | 'queryKey' | 'queryFn'
  >
}

/**
 * @param type - The resource type.
 * @param id - The resource id.
 * @param options - The options.
 * @returns The query result.
 * @deprecated Use useRead instead
 */
export function useFhirRead<T extends ResourceType>(
  type: T | undefined,
  id?: Reference<ExtractResource<T>> | string | null,
  options?: UseFhirReadOptions<T>
): UseQueryResult<ExtractResource<T>> {
  const medplum = useMedplum()
  const resolvedId = (isReference(id) ? resolveId(id) : (id as string)) ?? ''

  const enabled =
    Boolean(type) &&
    Boolean(resolvedId) &&
    (options?.query?.enabled === undefined || options?.query?.enabled)
  const result = useQuery<ExtractResource<T>>({
    ...(options?.query as any),
    enabled,
    queryKey: cacheKeys.read(type, resolvedId),
    queryFn: () => {
      if (!type) {
        throw new Error('Resource type is undefined')
      }
      return medplum.readResource(type, resolvedId)
    },
    meta: {
      ...options?.query?.meta,
      queryParams: {
        _id: resolvedId,
      },
      resourceType: type,
    },
  })

  // 410 Gone = resource deleted. Suppress toast (stale refs create noise and
  // intercept pointer events in e2e tests); keep console.error for diagnostics.
  const isDeleted =
    !!result.error && isGone(normalizeOperationOutcome(result.error))

  useErrorToast(
    result.error &&
      `Error accessing ${type}/${resolvedId}: ${result.error.message ?? 'Unknown error'}`,
    { silent: isDeleted }
  )

  return result
}
